import { create } from 'zustand';
import type {
  BillingSummary,
  CheckoutSession,
  PaymentMethod,
  PlanId,
  ReferralSummary,
} from '@ai-media/shared';
import { api, apiErrorMessage } from '@/lib/api';
import { toast } from './ui.store';
import { useAuthStore } from './auth.store';
import { openRazorpayCheckout, type RazorpayResponse } from '@/lib/razorpay';

interface BillingState {
  summary: BillingSummary | null;
  referral: ReferralSummary | null;
  loading: boolean;
  /** Plan/method currently being paid for, so the button can show a spinner. */
  checkingOut: PlanId | null;
  load: () => Promise<void>;
  /**
   * Run the full UPI/card checkout for a plan.
   *
   * Opens a server-priced checkout, waits for the gateway's success callback,
   * then asks the API to verify it. The plan is only upgraded by that verified
   * server response — the browser never decides what was bought.
   */
  checkout: (plan: PlanId, method: PaymentMethod) => Promise<boolean>;
  applyReferral: (code: string) => Promise<boolean>;
  cancelSubscription: () => Promise<void>;
}

export const useBillingStore = create<BillingState>((set, get) => ({
  summary: null,
  referral: null,
  loading: false,
  checkingOut: null,

  load: async () => {
    set({ loading: true });
    try {
      const { data } = await api.get<BillingSummary>('/billing');
      set({ summary: data, referral: data?.referral ?? null });
    } catch (error) {
      toast(apiErrorMessage(error), 'danger');
    } finally {
      set({ loading: false });
    }
  },

  checkout: async (plan, method) => {
    set({ checkingOut: plan });
    try {
      const { data: session } = await api.post<CheckoutSession>('/billing/checkout', {
        plan,
        method,
      });

      // Mock mode has no gateway to open, so confirm the order directly. The
      // server still runs the full capture path (subscription + referral credit).
      if (session?.mock) {
        await api.post(`/billing/checkout/${session.id}/verify`, {
          razorpayPaymentId: `mock_pay_${session.id}`,
          razorpayOrderId: session.providerOrderId,
          razorpaySignature: 'mock-signature',
        });
        toast('Payment simulated — your plan is now active', 'success');
        await get().load();
        return true;
      }

      // The gateway sheet is modal, so bridge its callbacks back into a promise
      // the caller can await.
      await new Promise<void>((resolve) => {
        const finish = () => {
          set({ checkingOut: null });
          resolve();
        };
        void openRazorpayCheckout({
          key: session.providerKeyId,
          amount: session.amountPaise,
          currency: session.currency,
          name: 'Social Media Autopilot',
          description: `${plan} plan`,
          order_id: session.providerOrderId,
          prefill: { email: useAuthStore.getState().user?.email },
          notes: { plan: session.plan, method: session.method },
          // UPI and cards are the two rails the product supports; the choice only
          // pre-selects the tab, the sheet still offers both.
          method: { upi: true, card: true },
          theme: { color: '#2c609d' },
          handler: async (response: RazorpayResponse) => {
            try {
              await api.post(`/billing/checkout/${session.id}/verify`, response);
              toast('Payment received — your plan is now active', 'success');
              await get().load();
            } catch (error) {
              toast(apiErrorMessage(error), 'danger');
            } finally {
              finish();
            }
          },
          modal: {
            // Dismissed before paying: close out the orphaned order so it does
            // not linger in the history as a pending charge.
            ondismiss: () => {
              void api.post(`/billing/checkout/${session.id}/cancel`).catch(() => undefined);
              finish();
            },
          },
        }).catch((error) => {
          toast(apiErrorMessage(error), 'danger');
          finish();
        });
      });
      return true;
    } catch (error) {
      toast(apiErrorMessage(error), 'danger');
      return false;
    } finally {
      set({ checkingOut: null });
    }
  },

  applyReferral: async (code) => {
    try {
      const { data } = await api.post<ReferralSummary>('/billing/referral/apply', { code });
      set({ referral: data });
      toast(`Referral applied — you earn credit when ${code} subscribes`, 'success');
      await get().load();
      return true;
    } catch (error) {
      toast(apiErrorMessage(error), 'danger');
      return false;
    }
  },

  cancelSubscription: async () => {
    try {
      await api.post('/billing/cancel');
      toast('Subscription cancelled — access continues to the end of the period', 'medium');
      await get().load();
    } catch (error) {
      toast(apiErrorMessage(error), 'danger');
    }
  },
}));